import React, {useEffect, useState} from "react";
import {
    Typography,
    Box,
    Button,
    IconButton,
    CircularProgress
} from "@mui/material";
import {FilterRow, joins, operators} from "../FilterRow";
import add from "../../../../public/assets/icons/add.svg";
import theme from "../../../theme/Theme";
import {caseContext} from "../../organisms/workItemsTable";
import {
    codeTypeMap,
    dictionaryValueMap,
    fetchCodeSearch,
    getAllQARules,
    getUnrestrictedDictionary
} from "../../../utils/api";
import {SearchProviders} from "../../../queries/QAruleData";
import {policiesContext, serviceLineMapper, WidgetConstants} from "../../../utils/common";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import { StrictModeDroppable } from "../StrictModeDroppable";

let onSearchController = new AbortController();
let abortSignal = onSearchController.signal

//region Constants
const searchKeyMapper = {
    "CPT Codes": "procedure_code",
    "Note Specialty": "note_specialty",
    "Patient Class": "patient_class",
    "Patient Type": "patient_type",
    "Modifiers": "modifier",
    "ICD Codes": "diagnosis_code",
    "Billing Area": "billing_area",
    "Facility": "facility"
};
const hiddenFilters = ["qa_level", "automate_status"];
export const defaultColumnOptions = [
    {
        description: "Coder for the qa rule",
        entityType: "workitem",
        field: "owner",
        headerName: "Coder",
        id: "owner",
        sortable: true,
        width: 200
    },
    {
        description: "Assignee",
        entityType: "workitem",
        field: "assignee",
        headerName: "Assignee",
        id: "assignee",
        sortable: true,
        width: 200
    },
    {
        description: "Rule names",
        entityType: "workitem",
        field: "qa_collection_id",
        headerName: "Rule Name",
        id: "qa_collection_id",
        sortable: true,
        width: 200
    },
    {
        description: "Billing Provider on the invoice",
        entityType: "workitem",
        field: "billing_provider",
        headerName: "Billing Provider",
        id: "billing_provider",
        sortable: true,
        width: 200
    },
    {
        description: "Performing Provider on the invoice",
        entityType: "workitem",
        field: "performing_provider",
        headerName: "Performing Provider",
        id: "performing_provider",
        sortable: true,
        width: 200
    },
    {
        description: "",
        entityType: "workitem",
        field: "group",
        headerName: "Group",
        id: "group",
        sortable: true,
        width: 200
    },
    {
        description: "The service line of the tenant",
        entityType: "workitem",
        field: "service",
        headerName: "Service Line",
        id: "service",
        sortable: true,
        width: 200,
        selected: false
    },
    {
        description: "Queue",
        entityType: "workitem",
        field: "profile",
        headerName: "Queue",
        id: "profile",
        sortable: true,
        width: 200
    },
    // case_id is systemOnly=true in the directory schema, which excludes it from
    // recursiveBuildSchema() and the column definitions. We add it here explicitly
    // so it appears in the Filters dropdown without changing its systemOnly status,
    // which would expose it across all views (grids, forms, profiles, etc.).
    // modelName overrides the allColumnDefs lookup in appendModelNameToFilterKey,
    // which would otherwise resolve case_id to the workitem_event child table.
    {
        description: "Identifier for workitem",
        entityType: "workitem",
        field: "case_id",
        headerName: "Case ID",
        id: "case_id",
        modelName: "workitem",
        sortable: true,
        width: 275
    },
]
const codesearch = ["ICD Codes", "CPT Codes", "Modifiers"];
//endregion

//region Helper Functions
const checkAndAbortSearch = (abortSignal: AbortSignal) => {
    if (abortSignal.aborted) {
        onSearchController = new AbortController();
        abortSignal = onSearchController.signal;
    } else {
        onSearchController.abort();
        onSearchController = new AbortController();
        abortSignal = onSearchController.signal;
    }

    return abortSignal;
}

const mapCoders = (allCoders) => {
    return allCoders?.map(coder => coder.data.worker_id)
        .sort((coder1, coder2) => coder1.data?.first_name.localCompare(coder2.data?.first_name));
}

const mapQueues = (queues) => {
    return queues?.map(queue => queue.value)
        .sort((queue1, queue2) => queue1.localeCompare(queue2));
}

const mapGroups = (allGroups) => {
    return allGroups?.map(group => group.value)
        .sort((group1, group2) => group1.localeCompare(group2));
}

const mapCodersAndGroups = (allCoders, allGroups) => {
    const coders = mapCoders(allCoders);
    const groups = mapGroups(allGroups);
    return [...coders, ...groups];
  };

const searchProviders = async (searchTerm, client) => {
    const response = await client?.query({
        query: SearchProviders,
        variables: {
            name: Array.isArray(searchTerm) ? searchTerm[0] : searchTerm,
        },
        fetchPolicy: "no-cache",
    });
    if (response?.data?.SearchProviders?.data?.length === 0) return [];
    else {
        let data = response?.data?.SearchProviders?.data;
        data = data?.sort((a, b) => {
            if (a.prov_name < b.prov_name) return -1;
            if (a.prov_name > b.prov_name) return 1;
            return 0;
        });

        return data.map((row) => {
            return {
                value: row.prov_name,
                label: `${row.prov_name}: ${row.prov_id}`
            };
        });
    }
}
//endregion

/* 
    Filter options and values are built from:
        1. Dictionaries
        2. Existing workitem columns and values 
    
    Dictionary values take precedence over existing workitem columns and values
 */
export const Filters = (props: any) => {
    const {client, allCoders, allGroups, isEditable, isPopoverClosed, queues} = props;
    const {filters, setFilters, applyFilters, removeFilters} = props;
    const {optionsDict, setOptionsDict, columnOptions, directoryType, detailGridOptions} = props;

    const {userContext} = React.useContext(policiesContext);
    const serviceLineOptions = userContext.activeContext.serviceLines.map(serviceLine => {
        return {
            value: serviceLineMapper[serviceLine.name] ? serviceLineMapper[serviceLine.name] : serviceLine.name,
            label: serviceLine.name
        }
    });

    //region State
    const [loading, setLoading] = useState(true);
    const [uiFilters, setUiFilters] = useState(filters.filter(filter => !hiddenFilters.includes(filter.fieldId)).map((filter) => filter.filterIndex));
    const [clearFirstFilter, setClearFirstFilter] = useState(false);
    const [updatedColumnOptions, setUpdatedColumnOptions] = useState([]);
      const [updatedOptionsDict, setUpdatedOptionsDict] = useState({
        "owner": mapCoders(allCoders),
        "assignee": mapCodersAndGroups(allCoders ?? [], allGroups ?? []),
        "billing_provider": {
            onSearch: async (searchTerm, isGettingSelectedValue) => {
                return await searchProviders(searchTerm, client);
            }
        },
        "performing_provider": {
          onSearch: async (searchTerm, isGettingSelectedValue) => {
            return await searchProviders(searchTerm, client);
          }
        },
        "ordering_provider": {
          onSearch: async (searchTerm, isGettingSelectedValue) => {
            console.log("onSearch search term",searchTerm);
            return await searchProviders(searchTerm, client);
          }
        },
        "group": mapGroups(allGroups),
        "service": serviceLineOptions,
        "profile": mapQueues(queues),
      });
    //endregion

    //region Helpers
    const onFilterModified = (filter: any, apply?: boolean) => {
        if (Array.isArray(filter?.value) && !filter?.operator?.includes("in")) {
            filter = {
                ...filter,
                value: filter.value[0]
            }
        }

        const l = [];
        let flag = false;
        filters?.map((obj) => {
            if (obj?.filterIndex !== filter?.filterIndex) {
                l.push(obj);
            } else {
                flag = true;
                l.push(filter);
            }
        });
        if (!flag) {
            l.push(filter);
        }

        setFilters(l);
        if (apply) {
            applyFilters(l);
        }
    };

    const onAddFilter = () => {
        let newUiFilterPosition;

        if (uiFilters?.length) {
            newUiFilterPosition = uiFilters[uiFilters?.length - 1].filterIndex + 1;
        } else {
            newUiFilterPosition = 0;
        }

        onFilterModified({
            filterIndex: newUiFilterPosition,
            fieldName: updatedColumnOptions?.[0]?.field,
            fieldId: updatedColumnOptions?.[0]?.context?.id,
            operator: operators[0]?.value,
            value: undefined,
            join: uiFilters?.length !== 0
                ? joins[0]?.value === undefined ? "AND" : joins[0]?.value
                : undefined,
        });
    };

    const onFilterDelete = (uiFilter) => {
        const newFilters = filters?.filter(
            (filter) => filter.filterIndex != uiFilter
        );
        for (let i = 0; i < newFilters.length; i++) {
          newFilters[i].filterIndex = i;
          newFilters[i].join =
            i === 0 ? undefined : newFilters[i].join ?? "AND";
        }
        setFilters(newFilters);
        newFilters?.length ? applyFilters(newFilters) : removeFilters();
    };

    const handleDrop = (droppedItem) => {
        if (!droppedItem.destination) return;
        var updatedList = [...uiFilters];
        const destinationIndex = droppedItem.destination.index;
        const sourceIndex = droppedItem.source.index;
        const [reorderedItem] = updatedList.splice(sourceIndex, 1);
        updatedList.splice(destinationIndex, 0, reorderedItem);

        let draggedItemContent;
        let draggedOverItemContent;
        for (let filter of filters) {
            if (filter?.filterIndex === destinationIndex) {
                draggedOverItemContent = filter;
            } else if (filter?.filterIndex === sourceIndex) {
                draggedItemContent = filter;
            }
            if (draggedItemContent && draggedOverItemContent) {
                break;
            }
        }
        const l = [];
        filters?.map((filter) => {
            if (filter?.filterIndex === destinationIndex) {
                l.push({
                    ...draggedItemContent,
                    filterIndex: filter?.filterIndex,
                    join: filter?.join,
                });
            } else if (filter?.filterIndex === sourceIndex) {
                l.push({
                    ...draggedOverItemContent,
                    filterIndex: filter?.filterIndex,
                    join: filter?.join,
                });
            } else {
                l.push(filter);
            }
        });
        
        setFilters(l);
        applyFilters(l);
    };

    useEffect(() => {
        if (isPopoverClosed) {
          // updating state to reset the input value to the selected value when the popover closes
          setLoading(false);
        }
      }, [isPopoverClosed]);

    useEffect(() => {
        if (abortSignal.aborted) {
            onSearchController = new AbortController();
            abortSignal = onSearchController.signal;
        }

        /* Reach out to the unrestricted dictionary endpoint and grab all dictionary titles */
        const getAllDictionaryOptions = async () => {
            const token = JSON.parse(sessionStorage.getItem("tokenPayload"));
            const tenantId = token['tenantId']

            const tenantDictionaries = await getUnrestrictedDictionary(tenantId, "Dictionary Lookup Helper", abortSignal);
            let tenantNames = [];
            tenantDictionaries.rowValues.forEach(dictionary => {
                // Special Case, Patient Class and Patient Type are the same dictionary but two separate filters
                if (dictionary.Name === "Patient Class") {
                    tenantNames.push({
                        name: "Patient Class",
                        searchKey: "Patient Class"
                    });
                    tenantNames.push({
                        name: "Patient Type",
                        searchKey: "Patient Class",
                        modifier: (value: string) => value.toUpperCase()
                    });
                    return;
                }

                // General Case
                tenantNames.push({
                    name: dictionary.Name,
                    searchKey: dictionary.Name
                });
            });

            // Code search is included in the dictionary list but is handled separately
            tenantNames = tenantNames.filter(tenantName => !codesearch.includes(tenantName.name));

            let mappedDictionaries = tenantNames.map((tenantName, index) => {
                return {
                    description: "",
                    entityType: "workitem",
                    field: searchKeyMapper[tenantName.name] || tenantName.name.toLowerCase().replace(" ", "_"),
                    headerName: tenantName.name,
                    id: tenantName.name,
                    sortable: true,
                    width: 200
                };
            });

            return {
                mappedDictionaries,
                tenantNames
            }
        }

        const setupOptionsDict = async (opts) => {
            // Setup the options dictionary, this will be used to populate the values dropdowns in the filter row
            return {
                ...optionsDict,
                ...updatedOptionsDict,
                ...opts.tenantNames.reduce((acc, tenantName, index) => {
                    acc[tenantName.name] = {
                        onSearch: async () => {
                            // Abort any previous searches that are still running
                            // abortSignal = checkAndAbortSearch(abortSignal);

                            const token = JSON.parse(sessionStorage.getItem("tokenPayload"));
                            const tenantId = token['tenantId'];
                            const response = await getUnrestrictedDictionary(tenantId, tenantName.searchKey, abortSignal);
                            const formattedResults = {
                                rowValues: response.rowValues.map(row => {
                              if (tenantName.name === "Billing Area") {
                                return {
                                  [dictionaryValueMap[tenantName.name]]:  row[dictionaryValueMap[tenantName.name] ?? tenantName.name],
                                  id: row['Billing Area ID'] ??  row['Billing Area'],
                                }
                              } else if (tenantName.name === "Facility") {
                                return {
                                  id:  row[dictionaryValueMap[tenantName.name] ?? tenantName.name],
                                  [dictionaryValueMap[tenantName.name]]: row['Abbreviation'] ?? row[dictionaryValueMap[tenantName.name]],
                                }
                              }
                                    return {
                                        [dictionaryValueMap[tenantName.name]]: tenantName.modifier
                                            ? tenantName.modifier(row[dictionaryValueMap[tenantName.name]])
                                            : row[dictionaryValueMap[tenantName.name]]
                                    }
                                })
                            };

                            if (formattedResults.rowValues.length === 0) return [];
                            const sortKey = dictionaryValueMap[tenantName.name];
                            const sortedValues = formattedResults.rowValues.sort((a, b) => {
                                const aVal = a[sortKey] ?? '';
                                const bVal = b[sortKey] ?? '';
                                return String(aVal).localeCompare(String(bVal), 'en', { sensitivity: 'base' });
                            })
                            if (tenantName.name === "Billing Area" || tenantName.name === "Facility") {
                              const dedupedValues = sortedValues.filter((value, index, self) => 
                                self.findIndex(v => v[dictionaryValueMap[tenantName.name]] === value[dictionaryValueMap[tenantName.name]]) === index
                              );
                              return dedupedValues.map(value => {
                                return {
                                  value: value.id,
                                  label: value[dictionaryValueMap[tenantName.name]]
                                };
                              });
                            } else {
                                  const dedupedValues = sortedValues.map(row => row[dictionaryValueMap[tenantName.name]]).filter((value, index, self) => self.indexOf(value) === index);
                                  return dedupedValues.map(value => {
                                    return {
                                      value: value,
                                      label: value
                                    };
                                  });
                            }
                        }
                    };
                    return acc;
                }, {}),
                ...codesearch.reduce((acc, name, index) => {
                    acc[name] = {
                        onSearch: async (searchTerm) => {
                            // Abort any previous searches that are still running
                            // abortSignal = checkAndAbortSearch(abortSignal);

                            // Ensure some results are shown before any typing
                            let searchToken = searchTerm;
                            if (searchToken === "" && name !== "Modifiers") searchToken = "9";

                            const token = JSON.parse(sessionStorage.getItem("tokenPayload"));
                            const tenantId = token['tenantId']
                            const response = await fetchCodeSearch(tenantId, searchToken, codeTypeMap[name], abortSignal)
                            if (response.content.length === 0) return [];
                            return response.content.sort((a: any, b: any) => {
                                if (a.code < b.code)
                                    return -1;
                                else if (a.code > b.code)
                                    return 1;
                                else
                                    return 0;
                            }).map(row => {
                                return {
                                    value: row.code,
                                    label: `${row.code} - ${row.description}`
                                };
                            });
                        }
                    };
                    return acc;
                }, {})
            };
        }

        const setupColumnOptions = async (opts) => {
            // Setup the column options, this will be used to populate the options dropdown in the filter row
            const finalOpts = [
                ...defaultColumnOptions,
                ...opts.mappedDictionaries,
                ...codesearch.map((name, index) => ({
                    description: "",
                    entityType: "workitem",
                    field: searchKeyMapper[name],
                    headerName: name,
                    id: name,
                    sortable: true,
                    width: 200
                })),
            ];
            
            return finalOpts;
        }

        const init = async () => {
            setLoading(true);
            try {
                const opts = await getAllDictionaryOptions();
                const qaRules = await getAllQARules(client, userContext?.activeContext?.healthSystemTenantId, serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name);
                const dictionaryOptionsDict = await setupOptionsDict(opts);
                const dictionaryColumnOptions = await setupColumnOptions(opts);

                // If dictionary options exist for a column use that instead of the existingColumnOption + sorting and filtering
                const nonUniqueDictionaryOptions = dictionaryColumnOptions.filter(columnOption => {
                    return columnOptions.some(dictColumnOption => dictColumnOption.field === columnOption.field);
                });
                
                const combinedColumnOptions = [...nonUniqueDictionaryOptions, ...columnOptions];
                
                const filteredColumnOptions = combinedColumnOptions.filter((columnOption, index, self) =>
                        index === self.findIndex((t) => (
                            t.field === columnOption.field
                        ))
                );
                
                const sortedColumnOptions = filteredColumnOptions.sort((a, b) => {
                    if (a.headerName < b.headerName)
                        return -1;
                    else if (a.headerName > b.headerName)
                        return 1;
                    else
                        return 0;
                });
                
                const finalizedColumnOptions = sortedColumnOptions;

                // If dictionary options exist for a column use that instead of the existingOptionsDict, first case inside FilterRow is to check dictionary options, so no modification needed
                /* 
                    Provided options dict is usually just a list of IDs: [Empty Array], FilterRow handles getting reference data if the map -> [] is empty
                    FilterRow will first check for an onSearch (which dictionary options have) and then check for the ID in the options dict for cached
                    results, and finally if that is empty it will make a call to the backend to get the reference data
                */
                const combinedOptionsDict = {...optionsDict, ...dictionaryOptionsDict, "qa_collection_id": qaRules};
                const finalizedOptionsDict = combinedOptionsDict;
                
                setUpdatedColumnOptions(finalizedColumnOptions);
                setUpdatedOptionsDict(finalizedOptionsDict);
            } catch (error) {
                setUpdatedColumnOptions(columnOptions);
                setUpdatedOptionsDict(optionsDict);
                console.error(`Error getting criteria filters: ${error}`);
            }
            setLoading(false);
        }

        init();
    }, []);
    
    useEffect(() => {
        setUiFilters(filters.filter(filter => !hiddenFilters.includes(filter.fieldId)));
    }, [filters])
    
    return (
        <caseContext.Provider value={{
            directoryType: directoryType,
            columnOptions: updatedColumnOptions,
            detailGridOptions: {...detailGridOptions, columnDefs: updatedColumnOptions},
            optionsDict: updatedOptionsDict,
            setOptionsDict: (values) => {
                setUpdatedOptionsDict(values);
                setOptionsDict(values)
            },
        }}
        >
            <Box minHeight={"50px"} width={"100%"}>
                <Box
                    sx={{
                        border: props.hasBorder && `1px solid ${theme.palette.artisticStroke200.main}`,
                        borderRadius: "4px",
                        minWidth: "70%",
                        ...props.containerStyles
                    }}
                    id="criteria-filters"
                >
                    {props.title && (
                        <Typography style={{
                            fontSize: "14px",
                            marginLeft: "10px",
                            backgroundColor: "white",
                            width: "fit-content",
                        }}
                        >
                            {props.title}
                        </Typography>
                    )}

                    {loading ?
                        (
                            <Box sx={{
                                width: "250px",
                                height: "50px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center"
                            }}>
                                <CircularProgress color={"primary"} size={20}/>
                            </Box>
                        ) : (
                            <DragDropContext onDragEnd={handleDrop}>
                                <Box>
                                    {uiFilters?.length ? (
                                        <>
                                            <StrictModeDroppable droppableId="list-container">
                                                {(provided) => (
                                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                                        {uiFilters.map((uiFilter, index) => {
                                                            return (
                                                                <Box
                                                                    key={uiFilter.filterIndex + uiFilter.fieldName}
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "10px",
                                                                        margin: "10px 30px 10px 0px",
                                                                    }}
                                                                >
                                                                    <Draggable
                                                                        isDragDisabled={!isEditable}
                                                                        draggableId={uiFilter.filterIndex + uiFilter.fieldName}
                                                                        key={uiFilter.filterIndex + uiFilter.fieldName}
                                                                        index={index}
                                                                    >
                                                                        {(provided) => (
                                                                            <div
                                                                                style={{...provided.draggableProps.style}}
                                                                                ref={provided.innerRef}
                                                                                {...provided.dragHandleProps}
                                                                                {...provided.draggableProps}
                                                                            >
                                                                                <FilterRow
                                                                                    disabled={!isEditable}
                                                                                    client={client}
                                                                                    filterIndex={uiFilter.filterIndex}
                                                                                    resetAll={
                                                                                        uiFilter.filterIndex == 0
                                                                                            ? clearFirstFilter
                                                                                            : undefined
                                                                                    }
                                                                                    setResetAll={setClearFirstFilter}
                                                                                    onFilterModified={onFilterModified}
                                                                                    currentFilter={
                                                                                        uiFilter
                                                                                    }
                                                                                    onFilterDelete={onFilterDelete}
                                                                                    allCoders={allCoders}
                                                                                    allGroups={allGroups}
                                                                                    worklist={directoryType === WidgetConstants.WORKITEM}
                                                                                    queues={queues}
                                                                                />

                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                </Box>
                                                            );
                                                        })}

                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </StrictModeDroppable>
                                        </>
                                    ) : (
                                        <Typography sx={{color: "#abafb0", margin: "10px 0"}}>
                                            No filter conditions are applied
                                        </Typography>
                                    )}
                                </Box>
                            </DragDropContext>
                        )
                    }

                    <Box sx={{
                        display: "flex",
                        gap: "5px",
                        flexDirection: "row",
                        ml: "-16px",
                        padding: "0 0 10px 0"
                    }}
                    >
                        <Button
                            onClick={onAddFilter}
                            disableRipple={true}
                            sx={{textTransform: "none", height: "14px"}}
                            disabled={!isEditable}
                        >
                            <IconButton disableRipple={true}>
                                <img src={add} alt="add" style={{height: "14px"}}/>
                            </IconButton>
                            Add Condition
                        </Button>
                    </Box>
                </Box>
            </Box>
        </caseContext.Provider>
    )
}
